import { useEffect, useRef, useState } from "react";

/**
 * Fit-to-container scale hook.
 *
 * Observes a wrapper element and returns the largest `scale` (capped at 1) at
 * which a fixed `designW × designH` box fits inside it. Generalizes the
 * ResizeObserver pattern used by HeatMapZone — the basis for rendering a
 * minigame into a constant virtual coordinate space that looks (and plays)
 * identically at every screen size.
 *
 * @param {number} designW design-space width
 * @param {number} designH design-space height
 * @returns {{ wrapperRef: React.RefObject, scale: number }}
 */
export function useFitScale(designW, designH) {
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const compute = (w, h) => {
      if (w > 0 && h > 0) {
        setScale(Math.min(1, w / designW, h / designH));
      }
    };
    // Seed from the current size so the first paint isn't an un-scaled flash.
    const r = el.getBoundingClientRect();
    compute(r.width, r.height);
    const obs = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      compute(width, height);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [designW, designH]);

  return { wrapperRef, scale };
}

export default useFitScale;
